import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { 
  collection, 
  collectionGroup,
  onSnapshot, 
  doc, 
  addDoc, 
  deleteDoc 
} from "firebase/firestore";
import {
  FileText, User, Search, Plus, Trash2, IndianRupee, AlertCircle, 
  MapPin, RefreshCw, Layers, History, Calendar, Filter, 
  TrendingDown, CheckCircle2, Tag, Briefcase, Printer, Store, PieChart as PieChartIcon,
  ChevronDown
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Sidebar from "../components/Sidebar";

// Color palette for the Pie Chart
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function SuperAdminExpensesMatrix() {
  // --- TOP SYSTEM NAV TAB CONTROLLERS ---
  const [activeTab, setActiveTab] = useState("reportDashboard"); 
  
  // --- EXPORT & PREVIEW CONTROLLERS ---
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // --- REGISTRY DATA REPOSITORIES ---
  const [allStores, setAllStores] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [categories, setCategories] = useState(["Store Rent", "Salaries", "Utility Bills", "Lab Equipment", "Marketing"]);
  
  const [allEmployees, setAllEmployees] = useState([]); 
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [systemError, setSystemError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // --- FILTER STATES FOR REPORTS ---
  const [filterStore, setFilterStore] = useState("All Stores");
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [filterEmployee, setFilterEmployee] = useState("All Employees");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- FORM STATES: ADD EXPENSE (SUPER ADMIN CAN ADD TO ANY STORE) ---
  const [targetStoreId, setTargetStoreId] = useState("");
  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [chosenCategory, setChosenCategory] = useState("Store Rent");
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split("T")[0]);
  const [spentBy, setSpentBy] = useState("");
  const [paymentMode, setPaymentMode] = useState("UPI / Digital");
  const [expenseNotes, setExpenseNotes] = useState("");

  // --- FORM STATES: NEW CATEGORY ---
  const [newCategoryName, setNewCategoryName] = useState("");

  // --- 1. FETCH ALL STORES, EXPENSES, CATEGORIES (SUPER ADMIN LEVEL) ---
  useEffect(() => {
    // 1a. Fetch All Stores
    const unsubStores = onSnapshot(collection(db, "stores"), (snapshot) => {
      const storesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllStores(storesData);
      if (storesData.length > 0 && !targetStoreId) {
        setTargetStoreId(storesData[0].id); // Default for Add Expense form
      }
    });

    // 1b. Fetch ALL Expenses across ALL stores using collectionGroup
    const unsubExpenses = onSnapshot(collectionGroup(db, "expenses"), (snapshot) => {
      const expensesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllExpenses(expensesData);
      
      // Extract unique employees for the filter dropdown
      const uniqueEmployees = [...new Set(expensesData.map(exp => exp.spentBy).filter(Boolean))];
      setAllEmployees(uniqueEmployees);
    });

    // 1c. Fetch Global Categories
    const unsubCategories = onSnapshot(collection(db, "expense_categories"), (snapshot) => {
      if (!snapshot.empty) {
        setCategories(snapshot.docs.map(doc => doc.data().name));
      }
    });

    return () => { unsubStores(); unsubExpenses(); unsubCategories(); };
  }, []);

  // --- DYNAMIC FILTERED SPREADSHEET MATRIX ---
  const processedExpenses = useMemo(() => {
    return allExpenses.filter(exp => {
      if (filterStore !== "All Stores" && exp.branchId !== filterStore) return false;
      if (filterCategory !== "All Categories" && exp.category !== filterCategory) return false;
      if (filterEmployee !== "All Employees" && exp.spentBy !== filterEmployee) return false;
      
      const targetDate = exp.date;
      if (!targetDate) return true;

      if (startDate && targetDate < startDate) return false;
      if (endDate && targetDate > endDate) return false;

      return true;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allExpenses, filterStore, filterCategory, filterEmployee, startDate, endDate]);

  // --- DATA AGGREGATION FOR SUMMARY & CHARTS ---
  const { financialTotals, storeSummary, categorySummary } = useMemo(() => {
    let grandTotal = 0;
    const sSummary = {};
    const cSummary = {};

    processedExpenses.forEach(exp => {
      const amt = Number(exp.amount) || 0;
      grandTotal += amt;

      const storeName = allStores.find(s => s.id === exp.branchId)?.storeId || exp.branchId || "Unknown Store";
      if (!sSummary[storeName]) sSummary[storeName] = 0;
      sSummary[storeName] += amt;

      const cat = exp.category || "Uncategorized";
      if (!cSummary[cat]) cSummary[cat] = 0;
      cSummary[cat] += amt;
    });

    const pieData = Object.keys(cSummary).map(key => ({
      name: key,
      value: cSummary[key]
    })).sort((a, b) => b.value - a.value); 

    return { 
      financialTotals: { grandTotal }, 
      storeSummary: sSummary, 
      categorySummary: pieData 
    };
  }, [processedExpenses, allStores]);

  // --- PDF EXPORT HANDLER ---
  const handlePrintPDF = () => {
    window.print();
  };

  // --- BACKEND WRITE HANDLERS ---
  const handleCommitExpense = async (e) => {
    e.preventDefault();
    if (!targetStoreId || !expenseTitle.trim() || !expenseAmount || !spentBy.trim()) {
      setSystemError("Validation Intercept: Store, Title, Amount, and Employee are mandatory.");
      return;
    }

    setIsProcessing(true); setSystemError(null); setSuccessMessage("");

    try {
      const payload = {
        title: expenseTitle.trim(),
        amount: Number(expenseAmount),
        category: chosenCategory,
        date: expenseDate,
        spentBy: spentBy.trim(), 
        paymentMethod: paymentMode,
        notes: expenseNotes.trim(),
        branchId: targetStoreId,
        timestamp: new Date().toISOString()
      };

      await addDoc(collection(db, "stores", targetStoreId, "expenses"), payload);
      
      setSuccessMessage(`Expense successfully pushed to selected store's ledger.`);
      setExpenseTitle(""); setExpenseAmount(""); setSpentBy(""); setExpenseNotes("");
    } catch (err) {
      setSystemError(err.message || "Error saving transaction payload to Firebase.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const cleanName = newCategoryName.trim();
    if (!cleanName) return;

    if (categories.some(cat => cat.toLowerCase() === cleanName.toLowerCase())) {
      alert("Validation Warning: Expenditure category already active.");
      return;
    }

    try {
      await addDoc(collection(db, "expense_categories"), { name: cleanName });
      setNewCategoryName("");
      alert("New operational expense ledger index initiated safely.");
    } catch (err) {
      console.error("Error creating category code row: ", err);
    }
  };

  const handlePurgeExpense = async (exp) => {
    if (!window.confirm("Warning: Wiping this item permanently from core cloud logs?")) return;
    try {
      await deleteDoc(doc(db, "stores", exp.branchId, "expenses", exp.id));
    } catch (err) {
      console.error("Failure dropping database index object line: ", err);
    }
  };

  const getStoreName = (branchId) => {
    const store = allStores.find(s => s.id === branchId);
    return store ? store.storeId : "Unknown Store";
  };

  // --- RENDER REPORT CONTENT (Supports True Preview & Print) ---
  const renderReportContent = (isPrintPreview = false) => (
    <div className={`bg-white border border-slate-200 space-y-8 ${isPrintPreview ? 'p-0 md:p-0 shadow-none border-none rounded-none' : 'rounded-3xl p-6 md:p-8 shadow-sm print:border-none print:shadow-none print:rounded-none print:p-0'}`}>
      
      {/* Report Header */}
      <div className={`${isPrintPreview ? 'block' : 'hidden print:block'} mb-8 border-b-2 border-slate-800 pb-4`}>
        <h1 className="text-3xl font-black uppercase tracking-widest text-slate-900">Master Expense Report</h1>
        <p className="text-sm font-bold text-slate-500 mt-2">
          Filters Applied: 
          Store: {filterStore === "All Stores" ? "Combined" : getStoreName(filterStore)} | 
          Date: {startDate || "Beginning"} to {endDate || "Present"} | 
          Tag: {filterCategory}
        </p>
        <div className="mt-4 text-4xl font-black font-mono text-slate-900">
          Total: ₹{financialTotals.grandTotal.toLocaleString()}
        </div>
      </div>

      {/* SECTION 1: Grand Total & Summary Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${isPrintPreview ? 'hidden' : 'print:hidden lg:col-span-1'} bg-rose-50 rounded-2xl border border-rose-100 p-6 flex flex-col justify-center`}>
          <span className="text-xs text-rose-600 font-black uppercase tracking-widest block mb-2">Combined Net Outflow</span>
          <span className="block text-4xl font-black text-rose-600 font-mono tracking-tight">₹{financialTotals.grandTotal.toLocaleString()}</span>
          <span className="text-xs text-rose-500 mt-2 font-bold">Based on applied filters</span>
        </div>

        <div className={`lg:col-span-2 bg-slate-50 rounded-2xl border border-slate-200 p-6 ${isPrintPreview ? 'bg-white border-slate-300' : 'print:bg-white print:border-slate-300'}`}>
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-4 border-b border-slate-200 pb-2">Store-wise Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(storeSummary).map(([store, total]) => (
              <div key={store} className={`bg-white p-4 rounded-xl border border-slate-100 shadow-sm ${isPrintPreview ? 'border-slate-300 shadow-none' : 'print:border-slate-300 print:shadow-none'}`}>
                <span className="block text-[10px] uppercase font-bold text-slate-500 truncate">{store}</span>
                <span className="block text-lg font-black font-mono text-slate-800 mt-1">₹{total.toLocaleString()}</span>
              </div>
            ))}
            {Object.keys(storeSummary).length === 0 && (
              <div className="text-xs text-slate-400">No data available.</div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 2: Category Pie Chart */}
      <div className={`bg-white rounded-2xl border border-slate-200 p-6 ${isPrintPreview ? 'border-slate-300 break-inside-avoid shadow-none' : 'print:border-slate-300 print:break-inside-avoid print:shadow-none'}`}>
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-6 flex items-center gap-2">
          <PieChartIcon size={16} className="text-blue-500"/> Expenditure by Category
        </h3>
        {categorySummary.length > 0 ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categorySummary}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {categorySummary.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 'bold' }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-slate-400 text-xs font-bold">No categorical data to display.</div>
        )}
      </div>

      {/* SECTION 3: Full Detailed Summary Table */}
      <div className={`${isPrintPreview ? 'break-inside-auto' : 'print:break-inside-auto'}`}>
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 mb-4 flex items-center gap-2">
          <FileText size={16} className="text-emerald-500"/> Full Expenditure Log
        </h3>
        
        <div className={`overflow-x-auto text-[11px] border border-slate-200 rounded-xl ${isPrintPreview ? 'border-none rounded-none' : 'print:border-none print:rounded-none'}`}>
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className={`bg-slate-100 border-b border-slate-200 text-slate-600 font-black uppercase tracking-wider ${isPrintPreview ? 'bg-slate-200 border-slate-800' : 'print:bg-slate-200 print:border-slate-800'}`}>
                <th className="p-3 pl-4">Store / Branch</th>
                <th className="p-3">Title & Notes</th>
                <th className="p-3">Date</th>
                <th className="p-3">Category Tag</th>
                <th className="p-3">Authorized By</th>
                <th className="p-3 text-right">Amount (₹)</th>
                <th className={`p-3 text-center ${isPrintPreview ? 'hidden' : 'print:hidden'}`}>Action</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-slate-100 text-slate-700 font-medium ${isPrintPreview ? 'divide-slate-300' : 'print:divide-slate-300'}`}>
              {processedExpenses.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">No transactions found matching criteria.</td></tr>
              ) : (
                processedExpenses.map(exp => (
                  <tr key={exp.id} className={`hover:bg-slate-50 transition-all ${isPrintPreview ? 'break-inside-avoid' : 'print:break-inside-avoid'}`}>
                    <td className="p-3 pl-4 font-bold text-blue-600">
                      {getStoreName(exp.branchId)}
                    </td>
                    <td className="p-3 font-bold text-slate-800">
                      <span>{exp.title}</span>
                      {exp.notes && <span className="block text-[9px] text-slate-500 font-normal italic max-w-xs truncate mt-0.5">{exp.notes}</span>}
                    </td>
                    <td className="p-3 text-slate-600 font-mono">{exp.date}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-md text-[9px] bg-slate-100 text-slate-600 border border-slate-200 uppercase font-black tracking-wider ${isPrintPreview ? 'border-none p-0 bg-transparent' : 'print:border-none print:p-0 print:bg-transparent'}`}>
                        {exp.category}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{exp.spentBy} <span className="block text-[9px] text-slate-400 mt-0.5">via {exp.paymentMethod}</span></td>
                    <td className="p-3 text-right font-black text-slate-800 font-mono">₹{exp.amount?.toLocaleString()}</td>
                    <td className={`p-3 text-center ${isPrintPreview ? 'hidden' : 'print:hidden'}`}>
                      <button onClick={() => handlePurgeExpense(exp)} className="text-slate-300 hover:bg-rose-50 hover:text-rose-500 p-2 rounded-lg transition-colors"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200 ${isPreviewMode ? 'print:hidden' : ''}`}>
      {/* Hide Sidebar during PDF Export */}
      <div className="print:hidden">
        <Sidebar />
      </div>

      <div className="flex-1 p-4 lg:p-8 space-y-6 overflow-x-hidden print:p-0 print:m-0 print:w-full">
        
        {/* HEADER CONTROL PANEL (Hidden on Print) */}
        <div className="print:hidden flex flex-col xl:flex-row xl:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-6 shadow-sm">
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Layers className="text-blue-600" size={24} /> Super Admin Control
            </h1>
            <p className="text-xs font-bold text-slate-500 mt-1">Global Expenditure Analytics & Master Ledger</p>
          </div>

          <div className="flex flex-wrap gap-3 bg-slate-100 p-1.5 rounded-2xl border border-slate-200 text-xs font-bold shadow-inner">
            <button onClick={() => setActiveTab("reportDashboard")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "reportDashboard" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Combined Reports</button>
            <button onClick={() => setActiveTab("addExpense")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "addExpense" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Record Expense</button>
            <button onClick={() => setActiveTab("categorySettings")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "categorySettings" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Categories</button>
          </div>
        </div>

        {/* SYSTEM STATUS NOTIFIERS */}
        <div className="print:hidden">
          <AnimatePresence>
            {systemError && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-600 text-xs font-bold flex items-center gap-2 shadow-sm mb-4"><AlertCircle size={16} /> {systemError}</motion.div>
            )}
            {successMessage && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 text-xs font-bold flex justify-between items-center shadow-sm mb-4">
                <span className="flex items-center gap-2"><CheckCircle2 size={16}/> {successMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* =========================================
            TAB 1: SUPER ADMIN REPORT DASHBOARD 
        ========================================= */}
        {activeTab === "reportDashboard" && (
          <div className="space-y-6 print:space-y-4">
            
            {/* Action Bar (Filters & Print) */}
            <div className="print:hidden bg-white border border-slate-200 rounded-3xl p-5 shadow-sm flex flex-col lg:flex-row justify-between items-center gap-4">
              
              {/* FILTERS */}
              <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold w-full lg:w-auto">
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                  <Store size={12} className="text-slate-400" />
                  <select value={filterStore} onChange={e => setFilterStore(e.target.value)} className="bg-transparent text-slate-700 outline-none cursor-pointer">
                    <option value="All Stores">All Stores</option>
                    {allStores.map(s => <option key={s.id} value={s.id}>{s.storeId}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                  <Filter size={12} className="text-slate-400" />
                  <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="bg-transparent text-slate-700 outline-none cursor-pointer">
                    <option value="All Categories">All Tags</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                  <User size={12} className="text-slate-400" />
                  <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} className="bg-transparent text-slate-700 outline-none cursor-pointer">
                    <option value="All Employees">All Staff</option>
                    {allEmployees.map(emp => <option key={emp} value={emp}>{emp}</option>)}
                  </select>
                </div>

                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl">
                  <Calendar size={12} className="text-slate-400" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none text-slate-700 font-mono cursor-pointer" title="Start Date" />
                  <span className="text-slate-300">-</span>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none text-slate-700 font-mono cursor-pointer" title="End Date" />
                </div>
              </div>

              {/* EXPORT DROPDOWN MENU - FIXED HOVER ISSUE */}
              <div className="relative w-full lg:w-auto z-10">
                <button 
                  onClick={() => setShowExportDropdown(!showExportDropdown)} 
                  className="w-full lg:w-auto px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-xs shadow-md"
                >
                  <Printer size={16} /> Export <ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showExportDropdown && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-3 w-48 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden origin-top-right"
                    >
                      <button 
                        onClick={() => { setIsPreviewMode(true); setShowExportDropdown(false); }}
                        className="w-full text-left px-5 py-3.5 text-slate-700 hover:bg-slate-50 text-xs font-bold border-b border-slate-100 flex items-center gap-3 transition-colors"
                      >
                        <Search size={16} className="text-blue-500" /> Preview
                      </button>
                      <button 
                        onClick={() => { handlePrintPDF(); setShowExportDropdown(false); }}
                        className="w-full text-left px-5 py-3.5 text-slate-700 hover:bg-slate-50 text-xs font-bold flex items-center gap-3 transition-colors"
                      >
                        <FileText size={16} className="text-rose-500" /> Export (PDF)
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* ---- MAIN DASHBOARD RENDER ---- */}
            {!isPreviewMode && renderReportContent(false)}

          </div>
        )}

        {/* =========================================
            TAB 2: ADD EXPENSE (SUPER ADMIN)
        ========================================= */}
        {activeTab === "addExpense" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm print:hidden">
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 border-b border-slate-100 pb-3">
              <Briefcase size={16} className="text-blue-500" /> Record Outbound Cashflow (Global)
            </h2>
            <form onSubmit={handleCommitExpense} className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs">
              
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-rose-500 font-black uppercase tracking-wider text-[10px]">Target Store / Branch *</label>
                <select value={targetStoreId} onChange={e => setTargetStoreId(e.target.value)} className="w-full p-3 rounded-xl bg-rose-50 border border-rose-200 text-slate-900 font-bold outline-none focus:border-rose-500 cursor-pointer">
                  {allStores.map(store => (
                    <option key={store.id} value={store.id}>{store.storeId}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Expense Title *</label>
                <input type="text" placeholder="Ex: Shop Electrical Maintenance" value={expenseTitle} onChange={e => setExpenseTitle(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 focus:bg-white transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Amount (₹) *</label>
                <input type="number" placeholder="4500" value={expenseAmount} onChange={e => setExpenseAmount(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 font-mono font-bold outline-none focus:border-blue-500 focus:bg-white transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Ledger Category</label>
                <select value={chosenCategory} onChange={e => setChosenCategory(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 cursor-pointer">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Transaction Date</label>
                <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 font-mono outline-none focus:border-blue-500 cursor-pointer" />
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Authorized Staff Member *</label>
                <input type="text" placeholder="Employee Name" value={spentBy} onChange={e => setSpentBy(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 font-bold text-slate-800 outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Payment Method</label>
                <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 cursor-pointer">
                  <option>UPI / Digital</option>
                  <option>Cash Liquidity</option>
                  <option>Card Settlement</option>
                </select>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Remarks / Notes</label>
                <input type="text" placeholder="Add further explanatory details here..." value={expenseNotes} onChange={e => setExpenseNotes(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 focus:bg-white transition-colors" />
              </div>
              <div className="md:col-span-2 pt-2">
                <button type="submit" disabled={isProcessing} className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${isProcessing ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200" : "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200"}`}>
                  {isProcessing ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />} Commit Expenditure Record
                </button>
              </div>
            </form>
          </div>
        )}

        {/* =========================================
            TAB 3: CATEGORY SETTINGS
        ========================================= */}
        {activeTab === "categorySettings" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm print:hidden">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2"><Tag size={16} className="text-blue-500" /> Account Categories</h2>
              <p className="text-slate-500 text-xs mt-1 font-medium">Append custom billing tags directly into global selection listings.</p>
            </div>

            <form onSubmit={handleCreateCategory} className="flex flex-col sm:flex-row gap-3 text-xs">
              <input type="text" placeholder="Ex: Office Refreshments" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:bg-white focus:border-blue-500 transition-colors" />
              <button type="submit" className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shrink-0">
                <Plus size={16} /> Create Tag
              </button>
            </form>

            <div className="space-y-3 pt-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 block font-black">Active Registry</span>
              <div className="flex flex-wrap gap-2.5">
                {categories.map((cat, i) => (
                  <span key={i} className="px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-xl text-xs font-bold text-blue-700 flex items-center gap-1.5 shadow-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* =========================================
          FULL-SCREEN PRINT PREVIEW MODAL
      ========================================= */}
      <AnimatePresence>
        {isPreviewMode && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-800/80 backdrop-blur-sm overflow-y-auto print:bg-transparent print:backdrop-blur-none print:absolute print:z-0"
          >
            <div className="min-h-screen py-8 px-4 flex justify-center items-start print:p-0 print:m-0 print:block">
              <div className="max-w-5xl w-full bg-white shadow-2xl rounded-3xl relative overflow-hidden print:shadow-none print:rounded-none print:max-w-none print:w-full">
                
                {/* Preview Toolbar (Hidden in true print) */}
                <div className="sticky top-0 bg-slate-900 text-white p-5 flex justify-between items-center z-10 print:hidden border-b border-slate-800 shadow-md">
                  <div>
                    <h2 className="font-bold flex items-center gap-2 text-sm uppercase tracking-wider"><Search size={16} className="text-blue-400"/> Document Preview</h2>
                    <p className="text-[10px] text-slate-400 mt-1">This is exactly how your PDF will be formatted.</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={handlePrintPDF} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-colors shadow-sm flex items-center gap-2">
                      <FileText size={14}/> Export PDF
                    </button>
                    <button onClick={() => setIsPreviewMode(false)} className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold rounded-xl transition-colors shadow-sm">
                      Close Preview
                    </button>
                  </div>
                </div>

                {/* Render identical print view inside the Modal body */}
                <div className="p-8 md:p-12 print:p-0 print:m-0">
                  {renderReportContent(true)}
                </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}